import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, type SxProps } from '@mui/material';
import { faArrowsRotate } from '@fortawesome/sharp-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type Phase = 'loading' | 'settling' | 'done';

type AnimatedCounterProps = {
    value?: string | number;
    error?: boolean;
    onRetry?: () => void;
    sx?: SxProps;
};

const LOADING_INTERVAL_MS = 75;
const SETTLE_DURATION_MS = 700;

const AnimatedCounter: React.FunctionComponent<AnimatedCounterProps> = function ({
    value,
    error,
    onRetry,
    sx,
}) {
    const [displayed, setDisplayed] = useState<number>(0);
    const [phase, setPhase] = useState<Phase>('loading');
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const animFrameRef = useRef<number | null>(null);

    const clearTimers = () => {
        if (intervalRef.current !== null) clearInterval(intervalRef.current);
        if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
    };

    // Phase loading : chiffres aléatoires qui défilent
    useEffect(() => {
        if (value !== undefined || error) return undefined;

        setPhase('loading');
        intervalRef.current = setInterval(() => {
            setDisplayed(Math.floor(Math.random() * 1000));
        }, LOADING_INTERVAL_MS);

        return clearTimers;
    }, [value, error]);

    // Valeur reçue : animation de décompte vers la cible
    useEffect(() => {
        if (value === undefined || error) return undefined;
        if (typeof value !== 'number') {
            setPhase('done');
            return undefined;
        }

        clearTimers();
        setPhase('settling');

        const target = value;
        const startTime = performance.now();
        const startValue = displayed;

        const animate = (now: number) => {
            const elapsed = now - startTime;
            const t = Math.min(elapsed / SETTLE_DURATION_MS, 1);
            const eased = t === 1 ? 1 : 1 - 2 ** (-10 * t); // easeOutExpo
            const current = Math.round(startValue + (target - startValue) * eased);
            setDisplayed(current);

            if (t < 1) {
                animFrameRef.current = requestAnimationFrame(animate);
            } else {
                setDisplayed(target);
                setPhase('done');
            }
        };

        animFrameRef.current = requestAnimationFrame(animate);

        return clearTimers;
        // startValue capturé intentionnellement à l'instant du déclenchement
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, error]);

    if (error) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...sx }}>
                <Typography
                    component="span"
                    sx={{
                        color: 'error.main',
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        lineHeight: 'inherit',
                    }}
                >
                    Erreur
                </Typography>
                {onRetry && (
                    <IconButton
                        size="small"
                        onClick={onRetry}
                        aria-label="Réessayer"
                        sx={{ color: 'error.main', p: '2px' }}
                    >
                        <FontAwesomeIcon
                            icon={faArrowsRotate}
                            size="xs"
                        />
                    </IconButton>
                )}
            </Box>
        );
    }

    if (typeof value === 'string') {
        return (
            <Typography
                component="span"
                sx={sx}
            >
                {value}
            </Typography>
        );
    }

    return (
        <Typography
            component="span"
            sx={{
                fontVariantNumeric: 'tabular-nums',
                opacity: phase === 'loading' ? 0.35 : 1,
                transition: phase === 'done' ? 'opacity 0.3s ease' : 'none',
                ...sx,
            }}
        >
            {displayed}
        </Typography>
    );
};

export default AnimatedCounter;
